import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['source']

  static values = {
    text: String,
    copiedLabel: { type: String, default: 'Copied' }
  }

  async copy (event) {
    event.preventDefault()
    // Copy buttons live inside <summary> headers, where a click's default action
    // is toggling the disclosure. preventDefault stops that; stopPropagation keeps
    // the summary's own scroll-into-view handler from firing on a copy.
    event.stopPropagation()
    const button = event.currentTarget

    try {
      await this.write(this.copyText)
      this.showCopiedState(button)
    } catch (error) {
      console.error('Copy failed:', error)
      this.showFailedState(button)
    }
  }

  // Say so when the copy did not happen, rather than only in a tooltip.
  showFailedState (button) {
    const icon = button.querySelector('i')
    const originalTitle = button.title
    button.title = 'Copy failed'
    if (!icon) return

    const originalClass = icon.className
    icon.className = originalClass.replace('ti-copy', 'ti-alert-circle')
    setTimeout(() => {
      icon.className = originalClass
      button.title = originalTitle
    }, 1600)
  }

  // The async clipboard needs a secure context and permission; a self-hosted
  // install on plain HTTP, or a browser that denies it, falls back to the
  // older copy command on a hidden field.
  async write (text) {
    try {
      await navigator.clipboard.writeText(text)
    } catch (error) {
      const field = document.createElement('textarea')
      field.value = text
      field.setAttribute('readonly', '')
      field.style.position = 'fixed'
      field.style.opacity = '0'
      document.body.appendChild(field)
      field.select()
      const copied = document.execCommand('copy')
      field.remove()
      if (!copied) throw error
    }
  }

  // An explicit text value wins; otherwise copy what the source element renders.
  // Expansions hold their content in the DOM already, so echoing it into a data
  // attribute would duplicate payloads that run to thousands of characters.
  get copyText () {
    if (this.hasTextValue && this.textValue.length > 0) return this.textValue
    if (this.hasSourceTarget) return this.sourceTarget.innerText

    return ''
  }

  showCopiedState (button) {
    const icon = button.querySelector('i')

    if (icon) {
      const originalClass = icon.className
      icon.className = originalClass.replace('ti-copy', 'ti-check')
      button.disabled = true

      setTimeout(() => {
        icon.className = originalClass
        button.disabled = false
      }, 1200)

      return
    }

    const originalText = button.textContent
    button.textContent = this.copiedLabelValue
    button.disabled = true

    setTimeout(() => {
      button.textContent = originalText
      button.disabled = false
    }, 1200)
  }
}
